import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { FormField } from "@/components/ui/FormField";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { cn } from "@/utils/cn";
import type { AppKind, AppVersion } from "@/api/appVersions.api";
import { APP_LABEL } from "@/features/appVersions/types";
import { policyFormSchema, toPolicyFormValues, type PolicyFormValues } from "@/features/appVersions/policyForm";

export function PolicyModal({
  open,
  app,
  current,
  saving,
  onClose,
  onSubmit,
}: {
  open: boolean;
  app: AppKind;
  current: AppVersion | undefined;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (values: PolicyFormValues) => void;
}) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PolicyFormValues>({ resolver: zodResolver(policyFormSchema) });

  useEffect(() => {
    if (open) reset(toPolicyFormValues(current));
  }, [open, current, reset]);

  const values = watch();
  const forced = !!values.forceUpdate;
  const latest = current?.latestBuildNumber ?? null;
  const typed = Number(values.latestBuildNumber);
  const goesBackwards = latest !== null && Number.isFinite(typed) && typed > 0 && typed < latest;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`${APP_LABEL[app]} — update policy`}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button form="policy-form" type="submit" loading={saving}>
            Save policy
          </Button>
        </>
      }
    >
      <div className="grid gap-5 md:grid-cols-[1fr_13rem]">
        <form id="policy-form" className="flex flex-col gap-3" onSubmit={handleSubmit(onSubmit)}>
          <FormField
            label="Update type"
            hint={
              forced
                ? "Full-screen — users can't use the app until they update"
                : "Dismissible prompt — users can choose Later"
            }
          >
            <SegmentedControl
              size="md"
              options={[
                { value: "optional", label: "Optional" },
                { value: "forced", label: "Forced" },
              ]}
              value={forced ? "forced" : "optional"}
              onChange={(v) => setValue("forceUpdate", v === "forced", { shouldDirty: true })}
            />
          </FormField>

          <FormField
            label="Version"
            error={errors.latestBuildNumber?.message}
            hint={
              goesBackwards
                ? `Lower than the current version (${latest}) — users on a newer build won't be asked to update.`
                : "Build number of the release (the +N in pubspec). Same for Android and iOS."
            }
          >
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              placeholder="14"
              className="font-mono"
              error={!!errors.latestBuildNumber}
              {...register("latestBuildNumber")}
            />
          </FormField>

          <FormField label="Google Play URL" error={errors.androidStoreUrl?.message}>
            <Input error={!!errors.androidStoreUrl} {...register("androidStoreUrl")} />
          </FormField>
          <FormField label="App Store URL" error={errors.iosStoreUrl?.message}>
            <Input error={!!errors.iosStoreUrl} {...register("iosStoreUrl")} />
          </FormField>

          <FormField label="Release notes" error={errors.releaseNotes?.message} hint="Shown to users in the update prompt">
            <Textarea rows={3} {...register("releaseNotes")} />
          </FormField>
        </form>

        <div className="flex flex-col items-center gap-2">
          <div className="relative h-72 w-full overflow-hidden rounded-[1.4rem] border-4 border-ink bg-cream-200">
            <div className={cn("absolute inset-0 flex items-center justify-center p-3", forced ? "bg-cream-50" : "bg-ink/40")}>
              <div className={cn("w-full text-center", !forced && "rounded-xl bg-cream-50 p-3 shadow-popover")}>
                <div className="mx-auto mb-2 h-9 w-9 rounded-full bg-teal-50 text-lg leading-9">⬆</div>
                <p className="text-sm font-semibold text-ink">{forced ? "Update required" : "Update available"}</p>
                <p className="mt-1 line-clamp-5 text-[11px] leading-snug text-ink-muted">{values.releaseNotes || "—"}</p>
                <div className="mt-3 flex flex-col gap-1.5">
                  <span className="rounded-lg bg-gold-500 py-1.5 text-xs font-medium text-ink">Update now</span>
                  {!forced && <span className="rounded-lg py-1 text-xs text-ink-muted">Later</span>}
                </div>
              </div>
            </div>
          </div>
          <p className="text-center text-[11px] text-ink-faint">Rough preview — the app controls the final wording</p>
        </div>
      </div>
    </Modal>
  );
}
